import axios from "axios";
import { base_url } from "../../constants";
import { Axios } from "../../utils/axiosMiddleware";
import { SimpleCatchError } from "../../utils/Helper";

/**
 * Account session for the 1099 embedded components (Account management +
 * Documents). Returns `{clientSecret, accountId, hasEmail}`, or
 * `{noPayoutAccount: true}` for someone who has no connected account — a
 * homeowner, or a gigster who never set up payouts. That is an ordinary state
 * for the page to render, not an error, which is why it comes back as a value
 * rather than a rejection.
 *
 * The server reads `stripe_account_id` rather than provisioning one, so opening
 * the tax page never creates a Stripe account as a side effect.
 *
 * `token` mirrors `subscription.js`: inside the mobile WebView the page is
 * opened with a `?token=` deep link and there is no localStorage session, so
 * the request interceptor finds nothing to attach. Passing the token
 * explicitly is what makes the mobile path work at all — without it the call
 * 401s and the response interceptor clears storage and redirects home, taking
 * the user's session with it. Desktop passes nothing and the interceptor
 * handles it as usual.
 *
 * `code` is the one-time handoff code the mobile app puts in the link. It is
 * redeemed through a plain axios client rather than `Axios`: a used or expired
 * code answers 401, and the shared interceptor treats every 401 as a dead
 * session — it would clear localStorage and bounce home, logging out a desktop
 * user who merely followed a stale link. A rejected code comes back as
 * `{codeRejected: true}` for the page to render, with no toast and no side
 * effects. No Authorization header is sent; the code is the credential.
 *
 * The outcome says whether the code is still usable, because the page strips it
 * from the URL once it is spent and must not do that earlier:
 * - 401 / 400 / 404: refused (`codeRejected`), nothing left to retry.
 * - 429, any 5xx, no response: the code is still usable (`retryable`; 429 also
 *   sets `throttled`), so the same code may be sent again. The backend hands a
 *   code back when it cannot open the session, so a failure never costs it.
 */
const authConfig = (token) =>
  token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

const publicAxios = axios.create({ baseURL: base_url });

const REJECTED_STATUSES = [400, 401, 404];

const redeemFailure = (e) => {
  const status = e?.response?.status;
  if (REJECTED_STATUSES.includes(status)) {
    return { codeRejected: true };
  }
  return status === 429
    ? { retryable: true, throttled: true }
    : { retryable: true };
};

const redeemHandoffCode = async (code) => {
  try {
    const response = await publicAxios.post(
      "stripe/connect/tax-session/redeem",
      { handoffCode: code }
    );
    return response?.data;
  } catch (e) {
    return redeemFailure(e);
  }
};

export const createTaxSession = async (token, code) => {
  if (code) {
    return redeemHandoffCode(code);
  }
  try {
    const response = await Axios.post(
      "stripe/connect/tax-session",
      {},
      authConfig(token)
    );
    return response?.data;
  } catch (e) {
    SimpleCatchError(e);
  }
};
